import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Images, Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ImageMetaDialog, LibraryPickerDialog } from "@/components/imagenes/ImageDialogs";
import { ImageRow } from "@/components/imagenes/ImageRow";
import { api, errMsg } from "@/lib/api";

export const ImageManager = ({ articleId, images, featured, captions, imageMeta, onChange, onInsert }) => {
  const fileRef = useRef(null);
  const [urlInput, setUrlInput] = useState("");
  const [busy, setBusy] = useState("");
  const [picker, setPicker] = useState(false);
  const [libItems, setLibItems] = useState([]);
  const [libQuery, setLibQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [people, setPeople] = useState([]);
  const [meta, setMeta] = useState(null);
  const list = images || [];
  const caps = captions || {};
  const metas = imageMeta || {};

  const saveMeta = async () => {
    const { url, ...fields } = meta;
    try {
      const { data } = await api.put(`/articles/${articleId}/images`, {
        images: list, featured_image: featured ?? "", captions: caps,
        image_meta: { ...metas, [url]: fields },
      });
      onChange(data);
      setMeta(null);
      toast.success("Datos de la imagen guardados");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const loadLibrary = async () => {
    try {
      const { data } = await api.get(`/library?q=${encodeURIComponent(libQuery)}&limit=40`);
      setLibItems(data.items || []);
    } catch (e) {
      toast.error(errMsg(e));
    }
    try {
      const { data } = await api.get(`/articles/${articleId}/library-suggestions`);
      setSuggestions(data.items || []);
      setPeople(data.people || []);
    } catch {
      setSuggestions([]);
    }
  };

  const addFromLibrary = async (item) => {
    try {
      const fd = new FormData();
      fd.append("image_urls", item.url);
      const { data } = await api.post(`/articles/${articleId}/images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data);
      if (item.caption || item.title || item.alt_text) {
        const { data: updated } = await api.put(`/articles/${articleId}/images`, {
          images: data.images, featured_image: data.featured_image ?? "",
          captions: { ...caps, [item.url]: item.caption || "" },
          image_meta: {
            ...metas,
            [item.url]: {
              title: item.title || item.name || "", alt_text: item.alt_text || item.caption || "",
              caption: item.caption || "", description: item.description || "",
              credit: item.credit || "",
            },
          },
        });
        onChange(updated);
      }
      setPicker(false);
      toast.success("Imagen añadida desde la biblioteca");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const persist = async (next, nextFeatured = featured, nextCaps = caps, nextMetas = metas) => {
    const { data } = await api.put(`/articles/${articleId}/images`, {
      images: next,
      featured_image: nextFeatured ?? "",
      captions: nextCaps,
      image_meta: nextMetas,
    });
    onChange(data);
  };

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy("upload");
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/articles/${articleId}/images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data);
      toast.success(`${data.added.length} imagen(es) añadida(s)`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addByUrl = async () => {
    setBusy("url");
    try {
      const fd = new FormData();
      fd.append("image_urls", urlInput);
      const { data } = await api.post(`/articles/${articleId}/images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data);
      setUrlInput("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const transform = async (url, options, label) => {
    setBusy(url + label);
    try {
      const { data } = await api.post("/media/transform", { url, ...options });
      const next = list.map((u) => (u === url ? data.url : u));
      const nextCaps = { ...caps };
      if (nextCaps[url]) {
        nextCaps[data.url] = nextCaps[url];
        delete nextCaps[url];
      }
      // Los datos para WordPress viajan con la imagen a su nueva URL.
      const nextMetas = { ...metas };
      if (nextMetas[url]) {
        nextMetas[data.url] = nextMetas[url];
        delete nextMetas[url];
      }
      await persist(next, featured === url ? data.url : featured, nextCaps, nextMetas);
      toast.success(`Imagen ${label}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const move = async (index, delta) => {
    const next = [...list];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    await persist(next);
  };

  const remove = async (url) => {
    const nextCaps = { ...caps };
    delete nextCaps[url];
    await persist(list.filter((u) => u !== url), featured === url ? "" : featured, nextCaps);
    toast.success("Imagen eliminada de la nota");
  };

  const openMeta = (url) => setMeta({
    url,
    title: metas[url]?.title || "",
    alt_text: metas[url]?.alt_text || caps[url] || "",
    caption: metas[url]?.caption || caps[url] || "",
    description: metas[url]?.description || "",
    credit: metas[url]?.credit || "",
  });

  return (
    <div data-testid="image-manager">
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => upload(e.target.files)}
          data-testid="image-upload-input"
        />
        <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy === "upload"} data-testid="upload-images-btn">
          {busy === "upload" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          Subir imágenes
        </Button>
        <div className="flex gap-2 flex-1">
          <Input
            placeholder="Pegar URL de imagen"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            data-testid="image-url-input"
          />
          <Button variant="outline" onClick={addByUrl} disabled={!urlInput || busy === "url"} data-testid="add-image-url-btn">
            <ImagePlus className="h-4 w-4" />
          </Button>
        </div>
        <Button variant="outline" onClick={() => { setPicker(true); loadLibrary(); }}
                data-testid="open-library-picker-btn">
          <Images className="h-4 w-4 mr-2" />Desde la biblioteca
        </Button>
      </div>

      <LibraryPickerDialog
        open={picker} onOpenChange={setPicker} libQuery={libQuery} setLibQuery={setLibQuery}
        loadLibrary={loadLibrary} libItems={libItems} suggestions={suggestions}
        people={people} onPick={addFromLibrary}
      />

      <div className="space-y-4">
        {list.map((url, i) => (
          <ImageRow
            key={url}
            url={url}
            index={i}
            total={list.length}
            isFeatured={url === featured}
            caption={caps[url] || ""}
            busy={busy.startsWith(url)}
            onFeature={() => persist(list, url)}
            onInsert={() => onInsert(url, caps[url] || "")}
            onEditMeta={() => openMeta(url)}
            onCaption={(value) => persist(list, featured, { ...caps, [url]: value })}
            onTransform={(options, label) => transform(url, options, label)}
            onMove={(delta) => move(i, delta)}
            onRemove={() => remove(url)}
          />
        ))}
        {list.length === 0 && (
          <p className="text-sm text-muted-foreground border border-dashed border-border p-8 text-center">
            La nota no tiene imágenes. Suba archivos o pegue una URL.
          </p>
        )}
      </div>

      <ImageMetaDialog meta={meta} setMeta={setMeta} onSave={saveMeta} />
    </div>
  );
};
